import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/sonner';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import ApartmentDetailsList from '@/features/apartments/components/ApartmentsDetails/ApartmentDetailsList';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import { cn } from '@/lib/utils';
import ShowIcon from '@/components/icons/ShowIcon';
import ApartmentDetailsPhotoReview from '@/features/apartments/components/ApartmentsDetails/ApartmentDetailsPhotoReview';
import { useApartmentDetailsApi } from '@/features/apartments/hooks/useApartmentDetailsApi';
import { apartmentsConfig } from '@/features/apartments/utils/apartmentsConfig';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import ECarousel from '@/components/everridge/carousel/ECarousel';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';

type Props = {
    id?: string;
    onClose: (state: boolean) => void;
};

const ApartmentsDetails: FC<Props> = ({ id, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [showPhotoReview, setShowPhotoReview] = useState(false);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();
    const userRole = useUserRole();

    const { apartmentDetailsData, isLoadingApartmentDetailsData } = useApartmentDetailsApi({ id });

    const { resetFilter } = useFinancesPageFilter();    

    if (!apartmentDetailsData) return null;

    const handleClose = () => {
        setIsOpen(false);
        resetFilter();
        setTimeout(onClose, 300);
    };

    const handlePhotoReview = (index: number) => {
        setSelectedPhotoIndex(index);
        setShowPhotoReview(true);
    };

    const photoUrls = apartmentDetailsData.photoUrls ?? [];

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
                <SheetHeader>
                    <SheetDescription />
                    <SheetTitle>
                        {!isMobile && (
                            <SheetClose
                                className={cn(
                                    'flex items-center justify-center cursor-pointer',
                                    isMobile && 'size-11 border border-popover-100 rounded-full',
                                )}
                            >
                                <MoveLeftIcon />
                            </SheetClose>
                        )}
                        {t('main.apartments.components.details.title')}
                        {isMobile && (
                            <SheetClose className="size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full">
                                <Cancel_01_Icon />
                            </SheetClose>
                        )}
                    </SheetTitle>
                </SheetHeader>
                <div className="flex-1 flex flex-col bg-popover-30 overflow-hidden select-none">
                    <div
                        className={cn(
                            'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
                            isMobile && 'p-4 gap-4',
                        )}
                    >
                        <div
                            className={cn(
                                'shrink-0 h-73 rounded-2.5xl grid grid-cols-4 grid-rows-2 gap-2 overflow-hidden select-none',
                                (!photoUrls.length || photoUrls.length === 1) &&
                                    'grid-cols-1 grid-rows-1',
                                isMobile && 'grid-cols-1 grid-rows-1 h-50',
                            )}
                        >
                            {isMobile ? (
                                <ECarousel>
                                    {photoUrls?.length ? (
                                        photoUrls?.map((url, index) => (
                                            <EImageWithFallback
                                                key={index}
                                                src={url}
                                                fallbackSVG={ImageIconSolid}
                                            />
                                        ))
                                    ) : (
                                        <EImageWithFallback fallbackSVG={ImageIconSolid} />
                                    )}
                                </ECarousel>
                            ) : photoUrls?.length ? (
                                photoUrls.slice(0, 5).map((src, index) => (
                                    <div
                                        key={index}
                                        className={cn(
                                            'relative group cursor-pointer',
                                            apartmentsConfig.getImagesPosition(
                                                index,
                                                photoUrls.length,
                                            ),
                                        )}
                                        onClick={() => handlePhotoReview(index)}
                                    >
                                        <EImageWithFallback
                                            src={src}
                                            fallbackSVG={ImageIconSolid}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-popover-1000/20 active:bg-primary-color-50 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ShowIcon className="size-8 stroke-popover-10" />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <EImageWithFallback fallbackSVG={ImageIconSolid} />
                            )}
                        </div>
                        <div className="flex flex-col gap-1">
                            <h3 className={cn(isMobile && 'text-base')}>
                                {apartmentDetailsData.name}
                            </h3>
                            {userRole === Roles.admin && (
                                <p className="text-base text-popover-800">
                                    {apartmentDetailsData.ownerName}
                                </p>
                            )}
                        </div>
                        <ApartmentDetailsList
                            data={apartmentDetailsData}
                            isLoading={isLoadingApartmentDetailsData}
                        />
                    </div>
                </div>
                <Toaster />
            </SheetContent>
            {showPhotoReview && (
                <ApartmentDetailsPhotoReview
                    selectedIndex={selectedPhotoIndex}
                    data={photoUrls}
                    onClose={() => setShowPhotoReview(false)}
                />
            )}
        </Sheet>
    );
};

export default ApartmentsDetails;
